/**
 * 字段联动引擎
 * 实现类似 Formily 的联动机制，支持声明式字段联动
 */

import { FormInstance } from 'antd';
import { useEffect, useRef } from 'react';
import {
  evaluateExpression,
  getNestedValue,
  createReactionExpressionContext,
} from './expression-engine';

/**
 * 字段联动响应配置
 * 参考 Formily 的 x-reactions 设计
 */
export interface FieldReaction {
  /**
   * 依赖的字段名列表
   * 当这些字段的值发生变化时，会触发联动
   */
  dependencies: string[];

  /**
   * 当依赖变化时的响应动作
   * 可以修改字段的各种属性：disabled, hidden, value 等
   */
  fulfill: {
    /**
     * 修改字段的状态属性（如 disabled, hidden 等）
     * 值可以是：
     * - 静态值：true, false, 'string'
     * - 表达式字符串：使用 {{ expression }} 包裹，支持 $deps, $self 等变量
     *   - $deps[0] 表示第一个依赖字段的值
     *   - $deps[1] 表示第二个依赖字段的值
     *   - $self 表示当前字段的值
     *   - 示例：{{ !$deps[0] }} 表示第一个依赖字段取反
     */
    state?: Record<string, any | string>;
    /**
     * 修改字段的值
     * 值可以是静态值或表达式字符串
     */
    value?: any | string;
  };
}

/**
 * 字段联动配置
 * 一个字段可以配置多个联动规则
 */
export type FieldReactions = FieldReaction[];


/**
 * 应用联动规则的结果
 */
export interface ApplyReactionResult {
  /**
   * 字段值的更新
   * 会通过 formInstance.setFieldsValue 应用
   */
  valueUpdate?: any;
  /**
   * Schema 层面的更新
   * 除了 value 之外的所有更新都在 Schema 层面处理
   * 支持的字段：fieldProps, labelTools, width, hidden, disabled, deleteable 等所有 TdsFormItemSchema 的属性
   */
  schemaUpdates?: Record<string, any>;
}

/**
 * 应用联动规则
 * 根据依赖字段的值，更新目标字段的属性
 */
export function applyReaction(
  reaction: FieldReaction,
  _formInstance: FormInstance,
  fieldName: string,
  allValues: Record<string, any>,
): ApplyReactionResult {
  const { dependencies, fulfill } = reaction;

  // 创建表达式上下文（使用共享的表达式引擎）
  const context = createReactionExpressionContext(fieldName, dependencies, allValues);

  const result: ApplyReactionResult = {};

  // 应用 state 更新（所有更新都在 Schema 层面处理）
  if (fulfill.state) {
    const schemaUpdates: Record<string, any> = {};

    Object.keys(fulfill.state).forEach((key) => {
      const expr = fulfill.state![key];
      const evaluated = evaluateExpression(expr, context);

      // 所有字段都在 schema 层面更新，支持嵌套路径，如 fieldProps.options
      if (key.includes('.')) {
        // 嵌套路径，如 fieldProps.options
        const keys = key.split('.');
        let target = schemaUpdates;
        for (let i = 0; i < keys.length - 1; i++) {
          if (!target[keys[i]]) {
            target[keys[i]] = {};
          }
          target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = evaluated;
      } else {
        // 直接属性
        schemaUpdates[key] = evaluated;
      }
    });

    if (Object.keys(schemaUpdates).length > 0) {
      result.schemaUpdates = schemaUpdates;
    }
  }

  // 应用 value 更新
  if (fulfill.value !== undefined) {
    const newValue = evaluateExpression(fulfill.value, context);
    result.valueUpdate = newValue;
  }

  return result;
}

/**
 * 字段联动配置映射
 * fieldName -> reactions[]
 */
export type FieldReactionsMap = Map<string, FieldReactions>;

/**
 * 创建字段联动映射
 */
export function createReactionsMap(items:
Array<{ name: string | number | undefined; reactions?: FieldReactions
}>): FieldReactionsMap {
  const map = new Map<string, FieldReactions>();

  items.forEach((item) => {
    if (item.name && item.reactions) {
      map.set(String(item.name), item.reactions);
    }
  });

  return map;
}

/**
 * 检查依赖字段是否有变化
 */
function hasDependencyChanged(
  dependencies: string[],
  prevValues: Record<string, any>,
  currentValues: Record<string, any>,
): boolean {
  return dependencies.some((depName) => {
    const prevValue = getNestedValue(prevValues, depName);
    const currentValue = getNestedValue(currentValues, depName);

    // 使用 JSON.stringify 进行深度比较（简单实现）
    return JSON.stringify(prevValue) !== JSON.stringify(currentValue);
  });
}

/**
 * 字段联动 Hook
 * 监听表单字段变化，自动应用联动规则
 *
 * @param reactionsMap 字段联动配置映射
 * @param formInstance 表单实例
 * @param allValues 所有字段的当前值
 */
export function useFieldReactions(
  reactionsMap: FieldReactionsMap,
  formInstance: FormInstance,
  allValues: Record<string, any>,
): void {
  const prevValuesRef = useRef<Record<string, any>>({});
  const isApplyingRef = useRef(false);

  useEffect(() => {
    if (reactionsMap.size === 0) {
      return;
    }

    // 防止递归更新
    if (isApplyingRef.current) {
      return;
    }

    const currentValues = allValues;
    const prevValues = prevValuesRef.current;

    // 遍历所有配置了联动的字段
    const valuesToUpdate: Record<string, any> = {};
    reactionsMap.forEach((reactions, fieldName) => {
      reactions.forEach((reaction) => {
        // 检查依赖是否变化
        if (hasDependencyChanged(reaction.dependencies, prevValues, currentValues)) {
          const result = applyReaction(reaction, formInstance, fieldName, currentValues);
          // 只处理 value 更新（通过 FormInstance）
          // 注意：schema 更新需要通过组件层面的动态 schema 来处理
          if (result.valueUpdate !== undefined) {
            valuesToUpdate[fieldName] = result.valueUpdate;
          }
        }
      });
    });

    // 应用所有更新
    if (Object.keys(valuesToUpdate).length > 0) {
      isApplyingRef.current = true;

      // 批量更新字段值（仅 value 更新通过 FormInstance）
      formInstance.setFieldsValue(valuesToUpdate);

      // 重置标志，允许下次更新
      setTimeout(() => {
        isApplyingRef.current = false;
      }, 0);
    }

    // 更新缓存的值
    prevValuesRef.current = { ...currentValues };
  }, [reactionsMap, formInstance, allValues]);
}


/**
 * 检查是否是 FieldReaction 对象
 * FieldReaction 有 dependencies 和 fulfill 属性
 */
export function isFieldReaction(value: any): value is FieldReaction {
  return (
    value
    && typeof value === 'object'
    && Array.isArray(value.dependencies)
    && value.fulfill
    && typeof value.fulfill === 'object'
  );
}
